import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'

export enum DiasHorario {
  LUNES = 1 << 0,
  MARTES = 1 << 1,
  MIERCOLES = 1 << 2,
  JUEVES = 1 << 3,
  VIERNES = 1 << 4,
  SABADO = 1 << 5,
  DOMINGO = 1 << 6,
}

export enum EstadoHorario {
  INACTIVO = 0,
  ACTIVO = 1,
}

export interface IBloqueHorario {
  id: number
  hora_inicio: string // "HH:MM"
  hora_final: string // "HH:MM" — cruce de medianoche cuando hora_final < hora_inicio
  dias: number // bitmask con DiasHorario
  cabecera_id: number // FK a IHorario.id
}

export interface IHorario {
  id: number
  nombre: string
  descripcion: string
  estado: EstadoHorario
  es_preferente?: boolean // ⭐ Horario Principal / Preferente Muelle Prat para usuarios internos
  bloques: IBloqueHorario[]
}

/**
 * Evalúa si una fecha/hora dada se encuentra dentro de un horario.
 * Si un bloque tiene hora_final < hora_inicio, se considera que cruza medianoche.
 */
export function isDateInHorario(horario: IHorario, date: Date): boolean {
  // date.getDay(): 0=Domingo, 1=Lunes, ..., 6=Sábado
  // DiasHorario: LUNES=bit0, ..., DOMINGO=bit6
  const dayIndex = (date.getDay() + 6) % 7 // 0=Lunes, ..., 6=Domingo
  const currentDayBit = 1 << dayIndex
  const yesterdayBit = 1 << (dayIndex + 6) % 7
  const currentMins = date.getHours() * 60 + date.getMinutes()

  for (const bloque of horario.bloques) {
    const [startH, startM] = bloque.hora_inicio.split(':').map(Number)
    const [endH, endM] = bloque.hora_final.split(':').map(Number)
    const startMins = startH * 60 + startM
    const endMins = endH * 60 + endM

    const crossesMidnight = endMins < startMins

    if (!crossesMidnight) {
      // Bloque normal dentro del mismo día
      if ((bloque.dias & currentDayBit) !== 0 && currentMins >= startMins && currentMins <= endMins) {
        return true
      }
    } else {
      // Bloque nocturno: activo desde hora_inicio del día indicado hasta hora_final del día siguiente
      // Fase 1: hoy es el día de inicio del bloque y la hora está después de hora_inicio
      if ((bloque.dias & currentDayBit) !== 0 && currentMins >= startMins) return true
      // Fase 2: hoy es el día siguiente del bloque (ayer tenía el bit) y la hora está antes de hora_final
      if ((bloque.dias & yesterdayBit) !== 0 && currentMins <= endMins) return true
    }
  }
  return false
}

/** Convierte "HH:MM" al formato ISO datetime que espera el backend (≥19 chars) */
function timeToISOString(time: string): string {
  return `1970-01-01T${time}:00.000Z`
}

/** Extrae "HH:MM" de un string de fecha ISO o de un string "HH:MM" directo */
function parseTimeString(raw: string): string {
  if (!raw) return '00:00'
  // Si viene como ISO datetime (ej. "2026-02-27T20:46:22.553Z"), extraer hora local
  if (raw.includes('T')) {
    const d = new Date(raw)
    const h = d.getUTCHours().toString().padStart(2, '0')
    const m = d.getUTCMinutes().toString().padStart(2, '0')
    return `${h}:${m}`
  }
  // Si ya viene como "HH:MM"
  return raw.substring(0, 5)
}

export default class HorariosServices {
  private bucketID: string | null = null

  constructor() {
    this.bucketID = dataStorageManager.registerBucket<IHorario>(this.getAllHorarios.bind(this), {
      refreshRatio: 5 * 60 * 1000,
      bucketID: BucketDefaultIDs.HORARIOS,
      operations: new Map<string, DMS_Operation>([
        [
          'insertHorario',
          {
            operation: this.insertHorario.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'updateHorario',
          {
            operation: this.updateHorario.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'insertBloqueHorario',
          {
            operation: this.insertBloqueHorario.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'updateBloqueHorario',
          {
            operation: this.updateBloqueHorario.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'deleteBloqueHorario',
          {
            operation: this.deleteBloqueHorario.bind(this),
            reloadAfter: true,
          },
        ],
      ]),
    })
  }

  startup() {}

  async getAllHorarios(): Promise<IHorario[]> {
    try {
      const [horariosResponse, bloquesResponse] = await Promise.all([
        dataQueryManager.addQuery<IHorario | IHorario[]>({
          query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_select_horario`,
          operation: 'POST',
          headers: { 'Content-Type': 'application/json' },
          params: [],
        }),
        dataQueryManager.addQuery<IBloqueHorario | IBloqueHorario[]>({
          query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_select_bloque_horario`,
          operation: 'POST',
          headers: { 'Content-Type': 'application/json' },
          params: [],
        }),
      ])

      const rawHorarios: IHorario[] = horariosResponse.data
        ? Array.isArray(horariosResponse.data)
          ? horariosResponse.data
          : [horariosResponse.data]
        : []

      const rawBloques: IBloqueHorario[] = bloquesResponse.data
        ? Array.isArray(bloquesResponse.data)
          ? bloquesResponse.data
          : [bloquesResponse.data]
        : []

      // Normalizar tiempos y agrupar bloques por cabecera_id
      const bloquesPorHorario = new Map<number, IBloqueHorario[]>()
      for (const b of rawBloques) {
        const bloque: IBloqueHorario = {
          id: b.id,
          hora_inicio: parseTimeString(b.hora_inicio),
          hora_final: parseTimeString(b.hora_final),
          dias: b.dias,
          cabecera_id: b.cabecera_id,
        }
        if (!bloquesPorHorario.has(bloque.cabecera_id)) {
          bloquesPorHorario.set(bloque.cabecera_id, [])
        }
        bloquesPorHorario.get(bloque.cabecera_id)!.push(bloque)
      }

      let preferenteFound = false
      let result = rawHorarios.map((h) => {
        const isPref = (h as any).es_preferente === true
        let isTarget = false
        if (isPref && !preferenteFound) {
          preferenteFound = true
          isTarget = true
        }
        return {
          ...h,
          es_preferente: isTarget,
          bloques: bloquesPorHorario.get(h.id) ?? [],
        }
      })

      if (!preferenteFound && result.length > 0) {
        // Si ninguno estaba marcado previamente, marcar unicamente el primero
        result[0].es_preferente = true
      }

      // Si no existen en la BD aún, proveerlos como lista principal predeterminada
      if (result.length === 0) {
        result = [
          {
            id: 1,
            nombre: 'Horario Muelle Verano 01',
            descripcion: 'Horario operacional Muelle Prat temporada de verano (08:00 - 23:59)',
            estado: EstadoHorario.ACTIVO,
            es_preferente: true,
            bloques: [
              {
                id: 101,
                hora_inicio: '08:00',
                hora_final: '23:59',
                dias: 127, // Lunes a Domingo (all days)
                cabecera_id: 1,
              },
            ],
          },
          {
            id: 2,
            nombre: 'Horario invierno 2026',
            descripcion: 'Horario operacional Muelle Prat temporada de invierno (08:00 - 22:00)',
            estado: EstadoHorario.INACTIVO,
            es_preferente: false,
            bloques: [
              {
                id: 102,
                hora_inicio: '08:00',
                hora_final: '22:00',
                dias: 127,
                cabecera_id: 2,
              },
            ],
          },
        ]
      }

      return result
    } catch (error) {
      console.error('Error fetching horarios:', error)
    }

    return [
      {
        id: 1,
        nombre: 'Horario Muelle Verano 01',
        descripcion: 'Horario operacional Muelle Prat temporada de verano (08:00 - 23:59)',
        estado: EstadoHorario.ACTIVO,
        es_preferente: true,
        bloques: [
          {
            id: 101,
            hora_inicio: '08:00',
            hora_final: '23:59',
            dias: 127,
            cabecera_id: 1,
          },
        ],
      },
      {
        id: 2,
        nombre: 'Horario invierno 2026',
        descripcion: 'Horario operacional Muelle Prat temporada de invierno (08:00 - 22:00)',
        estado: EstadoHorario.INACTIVO,
        es_preferente: false,
        bloques: [
          {
            id: 102,
            hora_inicio: '08:00',
            hora_final: '22:00',
            dias: 127,
            cabecera_id: 2,
          },
        ],
      },
    ]
  }

  async insertHorario(horarioData: IHorario): Promise<boolean> {
    try {
      const body = {
        op_insert_horario: {
          nombre: horarioData.nombre,
          descripcion: horarioData.descripcion,
          estado: horarioData.estado,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_insert_horario`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error inserting horario:', error)
    }
    return false
  }

  async updateHorario(horarioData: IHorario): Promise<boolean> {
    try {
      const body = {
        op_update_horario: {
          nombre: horarioData.nombre,
          descripcion: horarioData.descripcion,
          estado: horarioData.estado,
          id: horarioData.id,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_update_horario`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error updating horario:', error)
    }
    return false
  }

  async insertBloqueHorario(bloqueData: IBloqueHorario): Promise<boolean> {
    try {
      const body = {
        op_insert_bloque_horario: {
          hora_inicio: timeToISOString(bloqueData.hora_inicio),
          hora_final: timeToISOString(bloqueData.hora_final),
          dias: bloqueData.dias,
          cabecera_id: bloqueData.cabecera_id,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_insert_bloque_horario`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error inserting bloque horario:', error)
    }
    return false
  }

  async updateBloqueHorario(bloqueData: IBloqueHorario): Promise<boolean> {
    try {
      const body = {
        op_update_bloque_horario: {
          hora_inicio: timeToISOString(bloqueData.hora_inicio),
          hora_final: timeToISOString(bloqueData.hora_final),
          dias: bloqueData.dias,
          cabecera_id: bloqueData.cabecera_id,
          id: bloqueData.id,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_update_bloque_horario`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error updating bloque horario:', error)
    }
    return false
  }

  async deleteBloqueHorario(_bloqueData: IBloqueHorario): Promise<boolean> {
    // TODO: API op_delete_bloque_horario pendiente de implementación en backend
    console.warn('deleteBloqueHorario: API no disponible aún, operación omitida')
    return true
  }
}
